/**
 * 폴더 업로드 경로 계산 (2026-08-14) — `＋ 만들기 또는 업로드 → 폴더 업로드`.
 *
 * `<input webkitdirectory>` 는 고른 폴더 **안의 파일만** 평평하게 준다. 폴더 자체는 목록에
 * 없고, 각 파일의 `webkitRelativePath`("기획/1분기/보고서.docx")에만 구조가 남아 있다.
 * 그래서 업로드 전에 이 경로들로 폴더를 먼저 만들어야 한다 — 안 하면 중첩 구조가 통째로
 * 사라지고 파일 수십 개가 한 폴더에 쏟아진다.
 *
 * 이 모듈은 **순수 계산만** 한다(네트워크 없음). 호출부가 결과를 받아 순서대로 폴더를 만들고
 * 파일을 올린다.
 */

/** 업로드 대상 파일 한 건 — 브라우저 File 의 필요한 부분만 본다(테스트 용이). */
export interface RelPathFile {
  /** `webkitRelativePath`. 빈 문자열이면 루트 직속으로 취급한다. */
  webkitRelativePath?: string;
  name: string;
}

export interface FolderUploadPlan {
  /**
   * 만들어야 할 폴더 경로들 — **얕은 것부터** 정렬돼 있다.
   * 부모가 먼저 만들어져야 자식의 parentId 를 알 수 있으므로 순서가 곧 실행 순서다.
   */
  folders: string[];
  /** 파일 인덱스 → 들어갈 폴더 경로(루트면 ''). */
  fileDir: string[];
  /** 최상위 폴더 이름 (표시용). 없으면 null. */
  rootName: string | null;
}

/**
 * 경로를 정규화한다 — 앞뒤 슬래시와 빈 세그먼트, `.`/`..` 를 제거한다.
 *
 * ⚠ `..` 를 그대로 두면 폴더 이름이 `..` 인 폴더가 만들어진다. 브라우저가 그런 경로를 줄
 *   일은 없지만, 이 함수는 사용자 입력에 준하는 값을 다루므로 방어한다.
 */
function normalizeSegments(path: string): string[] {
  return path
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s !== '' && s !== '.' && s !== '..');
}

/**
 * 파일 목록에서 만들 폴더와 각 파일의 목적지를 계산한다.
 *
 * `webkitRelativePath` 의 **마지막 세그먼트는 파일 이름**이라 폴더가 아니다 — 이걸 빼먹으면
 * 파일마다 같은 이름의 빈 폴더가 하나씩 생긴다.
 */
export function planFolderUpload(files: readonly RelPathFile[]): FolderUploadPlan {
  const folderSet = new Set<string>();
  const fileDir: string[] = [];
  let rootName: string | null = null;

  for (const f of files) {
    const segments = normalizeSegments(f.webkitRelativePath ?? '');
    // 마지막 세그먼트 = 파일 이름 → 폴더 경로에서 제외.
    const dirSegments = segments.slice(0, -1);
    if (dirSegments.length > 0 && rootName === null) rootName = dirSegments[0]!;

    // 조상 경로를 전부 등록한다 — 중간 폴더에 파일이 하나도 없어도 구조는 남아야 한다.
    for (let i = 1; i <= dirSegments.length; i++) {
      folderSet.add(dirSegments.slice(0, i).join('/'));
    }
    fileDir.push(dirSegments.join('/'));
  }

  const folders = [...folderSet].sort((a, b) => {
    const da = a.split('/').length;
    const db = b.split('/').length;
    // 얕은 것 먼저 — 같은 깊이면 이름순(실행 순서를 결정적으로 만든다).
    return da !== db ? da - db : a.localeCompare(b);
  });

  return { folders, fileDir, rootName };
}

/** 경로의 부모 경로 (최상위면 ''). */
export function parentPathOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? '' : path.slice(0, i);
}

/** 경로의 마지막 세그먼트 = 폴더 이름. */
export function leafNameOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? path : path.slice(i + 1);
}
